import { alternadorEmPilulaClass, opcaoDoAlternadorClass } from "@brevi/ui-kit/superficies";
import { ToggleGroup } from "radix-ui";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * TUT-301 — o gesto certo para "escolher um entre poucos" (frequência, dia
 * da semana): um grupo de botões unidos, não um `<select>` que esconde as
 * opções atrás de um clique extra. Base: Radix `ToggleGroup` type="single"
 * (role="radiogroup" no grupo, role="radio" em cada botão — setas do teclado
 * vêm de graça do roving focus do Radix).
 *
 * `type="single"` do Radix permite DESSELECIONAR clicando de novo no item
 * ativo (emite ""). Este componente ignora esse caso: `onChange` só dispara
 * com um valor não vazio, então a escolha nunca fica vazia.
 *
 * `variante`: "segmentada" (padrão) é a fileira unida — frequência, dia da
 * semana. "grade" é para MUITAS opções (o dia do mês, 28 botões): cada botão
 * fica com a própria borda arredondada em vez da fileira contínua, porque
 * `not-first:border-l` só faz sentido numa única linha. "pilula" (TUT-482) é a
 * fileira do Lumen para poucas opções que TROCAM A LEITURA da tela (R$ | Qtd):
 * o grupo e a opção são pílulas, a escolhida é preenchida (o mesmo par
 * `primary` do botão da barra, TUT-472) e o fundo é o da pílula da casa,
 * `controleEmPilulaClass`.
 */
export function GrupoDeEscolha<T extends string>({
  value,
  onChange,
  opcoes,
  rotulo,
  variante = "segmentada",
  permiteLimpar = false,
  className,
}: {
  value: T;
  onChange: (valor: T) => void;
  /**
   * TUT-487a — `desabilitada`: a opção que não mudaria nada fica à vista, mas
   * não clicável (a regra da Linha do processo: "um filtro só aparece
   * habilitado se mudar o que se vê"). O MOTIVO é texto da tela, ao lado do
   * grupo, e não `title`, que o toque não mostra.
   */
  opcoes: { valor: T; rotulo: ReactNode; nomeAcessivel?: string; desabilitada?: boolean }[];
  /** aria-label do grupo — não há `<legend>` visível em cada uso. */
  rotulo: string;
  variante?: "segmentada" | "grade" | "pilula";
  /**
   * TUT-490d5a — o campo que NÃO é obrigatório (o questionário do KPI): clicar
   * de novo na opção escolhida a desmarca, e `onChange` recebe "" (o tipo do
   * valor precisa admitir o vazio). Sem a prop, a escolha nunca fica vazia.
   */
  permiteLimpar?: boolean;
  className?: string;
}) {
  const grade = variante === "grade";
  const pilula = variante === "pilula";
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(novo) => {
        if (novo || permiteLimpar) onChange(novo as T);
      }}
      aria-label={rotulo}
      className={cn(
        grade
          ? "grid grid-cols-7 gap-1"
          : pilula
            ? alternadorEmPilulaClass
            : "inline-flex overflow-hidden rounded-[6px] border border-[var(--border-strong)]",
        className,
      )}
    >
      {opcoes.map((opcao) => (
        <ToggleGroup.Item
          key={opcao.valor}
          value={opcao.valor}
          aria-label={opcao.nomeAcessivel}
          disabled={opcao.desabilitada}
          className={cn(
            pilula
              ? // TUT-511 — a opção do alternador em pílula (a receita de
                // `superficies.ts`, a mesma do `SectionBar` e do
                // `AlternadorEmPilula`); `data-state=on` marca a escolhida.
                // `flex-auto` pelo motivo da TUT-343, abaixo.
                cn(opcaoDoAlternadorClass, "flex-auto")
              : cn(
                  "min-h-[var(--control-h-compact)] min-w-8 cursor-pointer whitespace-nowrap px-2 text-corpo outline-0",
                  // TUT-343 — `flex-auto`, e não `flex-1`: com a base zerada as
                  // opções dividiam a fileira em partes IGUAIS, e a mais longa
                  // ("Semanal", em negrito quando escolhida) ficava mais larga que a
                  // parte dela e encostava na borda (Envios automáticos, 28/09).
                  grade ? "rounded-[6px] border border-[var(--border-strong)]" : "flex-auto px-3",
                  "text-[var(--text)] hover:bg-[var(--surface-2)]",
                  "disabled:cursor-not-allowed disabled:text-[var(--muted)] disabled:hover:bg-transparent",
                  "data-[state=on]:bg-[var(--primary-highlight)] data-[state=on]:font-semibold data-[state=on]:text-[var(--primary)]",
                  !grade && "not-first:border-l not-first:border-l-[var(--border-strong)]",
                  "transition-[background,color,border-color,transform] duration-[var(--dur-fast)]",
                  "active:scale-[0.97] motion-reduce:active:scale-100",
                  "focus-visible:border-focus focus-visible:shadow-[0_0_0_3px_var(--focus-highlight)]",
                ),
          )}
        >
          {opcao.rotulo}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
